import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Plus, Trash2 } from 'lucide-react';
import dayjs from 'dayjs';
import {
  LogNooraniSessionPayload,
  NooraniAttendanceApi,
  NooraniTeacherSessionRow,
} from '@/services/noorani-attendance.api';
import { NooraniGroupsApi } from '@/services/noorani-groups.api';
import { SchoolsApi } from '@/services/superadmin.api';
import { OrgPortalApi } from '@/services/org.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';

const columnHelper = createColumnHelper<NooraniTeacherSessionRow>();

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

/**
 * Noorani Teacher Attendance — a session's EXISTENCE is the attendance
 * record (no present/absent status); see NooraniClassSession's doc
 * comment. "Log Session" upserts on (group, date) — logging again for a
 * day that already has one edits it, matching the backend's upsert.
 */
export function NooraniTeacherAttendancePage({ scope }: { scope: 'superadmin' | 'organization' | 'school' }) {
  const qc = useQueryClient();
  const showSchoolPicker = scope !== 'school';
  const dt = useDataTable({ defaultLimit: 20 });

  const [schoolId, setSchoolId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [date, setDate] = useState('');
  const [logOpen, setLogOpen] = useState(false);
  const [confirm, setConfirm] = useState<{ open: boolean; row?: NooraniTeacherSessionRow }>({ open: false });

  useEffect(() => {
    setGroupId('');
  }, [schoolId]);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-noorani-attendance-filter', scope],
    queryFn: async () =>
      scope === 'organization' ? (await OrgPortalApi.listSchools({ limit: 100 })).items : (await SchoolsApi.list({ limit: 100 })).items,
    enabled: showSchoolPicker,
  });

  const { data: groupsPage } = useQuery({
    queryKey: ['noorani-groups-for-attendance-filter', schoolId, scope],
    queryFn: () => NooraniGroupsApi.list({ schoolId: showSchoolPicker ? schoolId || undefined : undefined, limit: 100 }),
    enabled: !showSchoolPicker || !!schoolId,
  });
  const groups = groupsPage?.items ?? [];

  const listParams = {
    ...dt.params,
    schoolId: showSchoolPicker ? schoolId || undefined : undefined,
    groupId: groupId || undefined,
    date: date || undefined,
  };
  const { data, isFetching } = useQuery({
    queryKey: ['noorani-attendance-teachers', listParams],
    queryFn: () => NooraniAttendanceApi.teachers(listParams),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['noorani-attendance-teachers'] });

  const remove = useMutation({
    mutationFn: (id: string) => NooraniAttendanceApi.deleteSession(id),
    onSuccess: () => {
      invalidate();
      setConfirm({ open: false });
    },
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor('groupName', { header: 'Group Name' }),
      ...(showSchoolPicker ? [columnHelper.accessor('schoolName', { id: 'schoolName', header: 'School' })] : []),
      columnHelper.display({
        id: 'teacherName',
        header: 'Teacher Name',
        cell: (info) => info.row.original.teacherName ?? <span className="text-slate-400">Unassigned</span>,
      }),
      columnHelper.accessor('date', { header: 'Date', cell: (info) => dayjs(info.getValue()).format('DD MMM YYYY') }),
      columnHelper.accessor('startTime', { header: 'Start Time' }),
      columnHelper.display({
        id: 'endTime',
        header: 'End Time',
        cell: (info) => info.row.original.endTime ?? '—',
      }),
      columnHelper.display({
        id: 'duration',
        header: 'Duration',
        cell: (info) => info.row.original.duration ?? '—',
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: (info) => (
          <button
            className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
            onClick={() => setConfirm({ open: true, row: info.row.original })}
            title="Delete session"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        ),
      }),
    ],
    [showSchoolPicker],
  );

  return (
    <>
      <PageHeader
        title="Noorani Teacher Attendance"
        description="Class sessions logged per group — a session's existence is the attendance record."
        actions={
          <button className="btn-primary" onClick={() => setLogOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" /> Log Session
          </button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {showSchoolPicker && (
          <Select className="!w-52" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
            <option value="">All schools</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        )}
        <Select className="!w-56" value={groupId} onChange={(e) => setGroupId(e.target.value)} disabled={showSchoolPicker && !schoolId}>
          <option value="">All groups</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </Select>
        <Input type="date" className="!w-40" value={date} onChange={(e) => setDate(e.target.value)} />
      </div>

      <DataTable
        columns={columns}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage="No sessions logged yet."
      />

      <LogSessionModal
        open={logOpen}
        onClose={() => setLogOpen(false)}
        onSaved={invalidate}
        schools={showSchoolPicker ? schools : undefined}
        defaultSchoolId={schoolId}
      />

      <ConfirmDialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false })}
        onConfirm={() => confirm.row && remove.mutate(confirm.row.id)}
        loading={remove.isPending}
        title="Delete this session?"
        message="This removes the logged session entirely."
        confirmText="Delete"
      />
    </>
  );
}

function LogSessionModal({
  open,
  onClose,
  onSaved,
  schools,
  defaultSchoolId,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  schools?: { id: string; name: string }[];
  defaultSchoolId?: string;
}) {
  const [schoolId, setSchoolId] = useState(defaultSchoolId ?? '');
  const [groupId, setGroupId] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');

  useEffect(() => {
    if (open) {
      setSchoolId(defaultSchoolId ?? '');
      setGroupId('');
      setTeacherId('');
      setDate(new Date().toISOString().slice(0, 10));
      setStartTime('');
      setEndTime('');
    }
  }, [open, defaultSchoolId]);

  const { data: groupsPage } = useQuery({
    queryKey: ['noorani-groups-for-log-session', schoolId],
    queryFn: () => NooraniGroupsApi.list({ schoolId: schools ? schoolId || undefined : undefined, limit: 100 }),
    enabled: open && (!schools || !!schoolId),
  });
  const groups = groupsPage?.items ?? [];
  const selectedGroup = groups.find((g) => g.id === groupId);

  useEffect(() => {
    if (selectedGroup) setTeacherId(selectedGroup.teacherId ?? '');
  }, [selectedGroup?.id]);

  const { data: teachers = [] } = useQuery({
    queryKey: ['noorani-teachers-for-log-session', schoolId || selectedGroup?.schoolId],
    queryFn: () => NooraniGroupsApi.teachersPicker(schoolId || selectedGroup?.schoolId),
    enabled: open && !!(schoolId || selectedGroup?.schoolId),
  });

  const save = useMutation({
    mutationFn: (payload: LogNooraniSessionPayload) => NooraniAttendanceApi.logSession(payload),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const valid = !!groupId && !!date && !!startTime;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Log Noorani Session"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={save.isPending || !valid}
            onClick={() =>
              save.mutate({ groupId, teacherId: teacherId || undefined, date, startTime, endTime: endTime || undefined })
            }
          >
            {save.isPending ? 'Saving…' : 'Log Session'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        {schools && (
          <Field label="School" required>
            <Select value={schoolId} onChange={(e) => { setSchoolId(e.target.value); setGroupId(''); }}>
              <option value="">— Select school —</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Noorani Group" required>
          <Select value={groupId} onChange={(e) => setGroupId(e.target.value)} disabled={!!schools && !schoolId}>
            <option value="">— Select group —</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Teacher">
          <Select value={teacherId} onChange={(e) => setTeacherId(e.target.value)} disabled={!groupId}>
            <option value="">— Unassigned —</option>
            {teachers.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Date" required>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start Time" required>
            <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </Field>
          <Field label="End Time">
            <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </Field>
        </div>
        {save.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(save.error)}</div>}
      </div>
    </Modal>
  );
}
